import type { PrelimsItemRecord } from '../types/tender.types';

export interface PrelimsVariant {
  label: string;
  code: string;
}

export interface PrelimsItem {
  description: string;
  unit: string;
  code: string;
  /** When set, this row's code is user-selectable via a dropdown (see the
   * "CBR testing option(s)" row) instead of being fixed by the mapping.
   * `code` above is the default selection. */
  variants?: PrelimsVariant[];
}

export interface PrelimsGroup {
  id: string;
  label: string;
  /** Optional explanatory note shown under the group label (matches the
   * descriptive text rows that sit under some group headers in the source
   * R&S Excel template). */
  description?: string;
  items: PrelimsItem[];
}

export interface PrelimsCategory {
  id: string;
  label: string;
  /** Optional explanatory note shown directly under the category label —
   * used when a category has a single implicit grouping (see
   * `groupedTableData` in PreliminaryItems.tsx, which folds a category's
   * lone group into its own row rather than showing a redundant nested
   * group header). */
  description?: string;
  /** Optional items that belong directly to the category rather than under
   * a named group. Most categories organise items under `groups`; this is
   * for the rarer case where a category needs no grouping at all. */
  items?: PrelimsItem[];
  groups: PrelimsGroup[];
}

export const PRELIMS_MAPPING: PrelimsCategory[] = [
  {
    id: 'method-related',
    label: 'METHOD RELATED ITEMS',
    // These items used to sit under named sub-category groups (Principal
    // Contractor, Statutory Applications, Goal Posts, Testing & Surveys,
    // Jetting & CCTV x3, Cleaning, Site Maintenance, Miscellaneous) — those
    // labels weren't wanted, so the items now sit directly on the category.
    // `buildPrelimRecord` in PreliminaryItems.tsx still falls back to each
    // item's old grouped id (`LEGACY_METHOD_RELATED_GROUP_IDS`) so saved
    // quantities/rates entered before this change keep loading correctly.
    items: [
      { description: 'Principal Contractor - Fixed Costs - Small', unit: 'Item', code: 'APRE002A' },
      { description: 'Principal Contractor - Fixed Costs - Standard', unit: 'Item', code: 'APRE002B' },
      { description: 'Principal Contractor - Fixed Costs - Large', unit: 'Item', code: 'APRE002C' },
      { description: 'Principal Contractor - Weekly Costs - Small', unit: 'Wks', code: 'APRE003A' },
      { description: 'Principal Contractor - Weekly Costs - Standard', unit: 'Wks', code: 'APRE003B' },
      { description: 'Principal Contractor - Weekly Costs - Large', unit: 'Wks', code: 'APRE003C' },
      { description: 'S106 applications', unit: 'Nr', code: 'SCPRE004' },
      { description: 'S50 applications', unit: 'Nr', code: 'SCPRE013' },
      { description: 'S184 applications', unit: 'Nr', code: 'SCPRE040' },
      { description: 'S171 applications', unit: 'Nr', code: 'SCPRE041' },
      { description: 'Goal Posts (GS6)', unit: 'Nr', code: 'APRE033' },
      {
        description: 'CBR tests',
        unit: 'Item',
        code: 'SCPRE006A',
        variants: [
          { label: 'CBR Mobilisation', code: 'SCPRE006' },
          { label: 'CBR Tests', code: 'SCPRE006A' },
        ],
      },
      { description: 'Binder and grading analysis', unit: 'Nr', code: 'SCPRE007' },
      { description: "NDT's", unit: 'Day', code: 'SCPRE008' },
      { description: 'Core testing', unit: 'Nr', code: 'SCPRE009' },
      { description: 'Jetting & CCTV of installed sewers; once', unit: 'm', code: 'SCPRE010' },
      { description: 'Jetting & CCTV of installed gully runs; once', unit: 'm', code: 'SCPRE010' },
      { description: 'Jetting & CCTV of installed sewers; prior to maintenance', unit: 'm', code: 'SCPRE010' },
      { description: 'Jetting & CCTV of installed gully runs; priore to maintenance', unit: 'm', code: 'SCPRE010' },
      { description: 'Jetting & CCTV of installed sewers; prior to adoption', unit: 'm', code: 'SCPRE010' },
      { description: 'Jetting & CCTV of installed gully runs; prior to adoption', unit: 'm', code: 'SCPRE010' },
      { description: 'Cleaning of manholes prior to surfacing', unit: 'Nr', code: 'APRE035' },
      { description: 'Cleaning of gullies prior to surfacing', unit: 'Nr', code: 'APRE036' },
      { description: 'Road Sweeping', unit: 'wks', code: 'APRE027' },
      { description: 'Vac-Ex', unit: 'wks', code: 'GWALPDS003' },
      { description: 'Wheel Wash', unit: 'wks', code: 'APRE037' },
    ],
    groups: [
      {
        id: 'dust-suppression',
        label: 'Dust Suppression',
        items: [
          { description: 'Dumper & Bowser', unit: 'wks', code: 'APRE034' },
          { description: 'Dust Cannons', unit: 'wks', code: 'APRE034A' },
        ],
      },
      {
        id: 'swm',
        label: 'SWM',
        items: [
          { description: 'Over Pumping - sewers', unit: 'wks', code: 'ALP224' },
          { description: 'Dewatering - lump sum S/c?', unit: 'Sum', code: 'SCSANC006' },
        ],
      },
      {
        id: 'swmp',
        label: 'SWMP',
        items: [
          { description: 'Silt Fencing', unit: 'm', code: 'APRE028' },
          { description: 'Headwall Barriers', unit: 'Nr', code: '' },
          { description: 'SWMP Management', unit: 'item', code: '' },
          { description: 'Supply Fuel for client forklift', unit: 'wks', code: '' },
          { description: 'Road plates', unit: 'wks', code: 'APRE021' },
          { description: 'Transport', unit: 'item', code: 'MPTR012' },
          { description: 'Demobilisation', unit: 'item', code: 'MPTR012' },
        ],
      },
      {
        id: 'traffic-management',
        label: 'Traffic Management',
        items: [
          { description: 'Road closure Application / Plan', unit: 'Item', code: 'SCPRE003' },
          { description: 'Road closure set up / take down / management', unit: 'Item', code: 'SCPRE002' },
          { description: 'Temp Traffic lights - 2 way', unit: 'wks', code: 'APRE038' },
          { description: 'Temp Traffic lights - 3 way', unit: 'wks', code: 'APRE039' },
          { description: 'Temp Traffic lights - 4 way', unit: 'wks', code: 'APRE040' },
          { description: 'Barrier / Signage', unit: 'wks', code: 'APRE021' },
          { description: 'Operatives for manual traffic control', unit: 'wks', code: 'APRE041' },
        ],
      },
      {
        id: 'engineering',
        label: 'Engineering',
        items: [
          { description: 'Set out works', unit: 'item', code: 'L003' },
          { description: 'As built Survey', unit: 'item', code: 'L003' },
        ],
      },
    ],
  },
  {
    id: 'tree-protection-fencing',
    label: 'TREE PROTECTION FENCING',
    groups: [
      {
        id: 'standard-tpf',
        label: 'Tree Protection Fencing As BS 5837:2012 Figure 3a',
        description: "Supply and erection of heras fencing on rubber feet.  Fencing to be supported every panel by a scaffold stabilizer strut at 45' with ground pins.",
        items: [
          { description: 'Supply of heras, rubber feet, clips and stabiliser strut', unit: 'm', code: 'APRE005' },
          { description: 'Erect heras fencing on feet', unit: 'm', code: 'APRE015' },
          { description: 'Dismantle heras fencing upon completion', unit: 'm', code: 'APRE016' },
        ],
      },
      {
        id: 'high-intensity-tpf',
        label: 'High Intensity Tree Protection Barrier',
        description: 'Supply and erect a scaffold frame driven 600mm into the ground, to which 3.5m heras fence panels are clipped and tied.',
        items: [
          { description: 'Supply of heras, scaffold poles, couplers & ties', unit: 'm', code: 'APRE011' },
          { description: 'Erect heras fencing on poles', unit: 'm', code: 'APRE017' },
          { description: 'Dismantle heras fencing upon completion', unit: 'm', code: 'APRE018' },
        ],
      },
    ],
  },
  {
    id: 'boundary-protection-fencing',
    label: 'BOUNDARY PROTECTION FENCING',
    groups: [
      {
        id: 'heras-fencing-rubber-feet',
        label: 'Supply and erect heras fencing on rubber feet.',
        // Shown as the category's note when this lone group is folded into
        // BOUNDARY PROTECTION FENCING's row (see groupedTableData in
        // PreliminaryItems.tsx) — explicit so single-group categories with
        // no real explanatory text (Security, Earthworks Validation) don't
        // pick up their group's label as a note by default.
        description: 'Supply and erect heras fencing on rubber feet.',
        items: [
          { description: 'Supply of heras, rubber feet & clips', unit: 'm', code: 'APRE012' },
          { description: 'Erect heras fencing on feet', unit: 'm', code: 'APRE019' },
          { description: 'Dismantle heras fencing upon completion', unit: 'm', code: 'APRE020' },
        ],
      },
    ],
  },
  {
    id: 'security',
    label: 'SECURITY',
    groups: [
      {
        id: 'security-services',
        label: 'Security Services',
        items: [
          { description: 'Guard', unit: 'Wks', code: 'SCSS001' },
          { description: 'CCTV', unit: 'Wks', code: 'SCSS004' },
          { description: 'Mobile response', unit: 'Wks', code: 'SCSS003' },
        ],
      }
    ],
  },
  {
    id: 'earthworks-validation',
    label: 'EARTHWORK VALIDATION',
    groups: [
      {
        id: 'validation-compliance',
        label: 'Validation & Compliance',
        items: [
          { description: 'Earthworks testing and validation plan', unit: 'item', code: 'SCEWT001' },
          { description: 'CL:AIRE fees & testing', unit: 'item', code: 'SCEWT002' },
          { description: 'Material Management statement', unit: 'item', code: 'SCEWT003' },
          { description: 'Laboritory testing', unit: 'item', code: 'SCEWT004' },
          { description: 'Site Supervision and compliance testing', unit: 'item', code: 'SCEWT005' },
          { description: 'Earthworks Validation report', unit: 'item', code: 'SCEWT006' },
        ],
      },
    ],
  },
];

// METHOD RELATED ITEMS items that used to sit under a named sub-category
// group (Principal Contractor, Statutory Applications, Goal Posts, Testing &
// Surveys, Jetting & CCTV x3, Cleaning, Site Maintenance, Miscellaneous)
// before those group labels were dropped and the items moved onto the
// category directly. Maps each item's description to its old group id so
// every place that builds this item's deterministic id
// (`prelim-${cat.id}-${idSuffix}`) can also check the old grouped id
// (`prelim-${cat.id}-${legacyGroupId}-${idSuffix}`) and keep finding
// quantities/rates a user saved before this change.
export const LEGACY_METHOD_RELATED_GROUP_IDS: Record<string, string> = {
  'Principal Contractor - Fixed Costs - Small': 'principal-contractor',
  'Principal Contractor - Fixed Costs - Standard': 'principal-contractor',
  'Principal Contractor - Fixed Costs - Large': 'principal-contractor',
  'Principal Contractor - Weekly Costs - Small': 'principal-contractor',
  'Principal Contractor - Weekly Costs - Standard': 'principal-contractor',
  'Principal Contractor - Weekly Costs - Large': 'principal-contractor',
  'S106 applications': 'statutory-applications',
  'S50 applications': 'statutory-applications',
  'S184 applications': 'statutory-applications',
  'S171 applications': 'statutory-applications',
  'Goal Posts (GS6)': 'goal-posts',
  'CBR tests': 'testing-surveys',
  'Binder and grading analysis': 'testing-surveys',
  "NDT's": 'testing-surveys',
  'Core testing': 'testing-surveys',
  'Jetting & CCTV of installed sewers; once': 'jetting-cctv-once',
  'Jetting & CCTV of installed gully runs; once': 'jetting-cctv-once',
  'Jetting & CCTV of installed sewers; prior to maintenance': 'jetting-cctv-maintenance',
  'Jetting & CCTV of installed gully runs; priore to maintenance': 'jetting-cctv-maintenance',
  'Jetting & CCTV of installed sewers; prior to adoption': 'jetting-cctv-adoption',
  'Jetting & CCTV of installed gully runs; prior to adoption': 'jetting-cctv-adoption',
  'Cleaning of manholes prior to surfacing': 'cleaning',
  'Cleaning of gullies prior to surfacing': 'cleaning',
  'Road Sweeping': 'site-maintenance',
  'Vac-Ex': 'site-maintenance',
  'Wheel Wash': 'site-maintenance',
  'Supply Fuel for client forklift': 'miscellaneous',
  'Road plates': 'miscellaneous',
  'Transport': 'miscellaneous',
  'Demobilisation': 'miscellaneous',
};

export function findPrelimsItem(code: string): PrelimsItem | undefined {
  for (const category of PRELIMS_MAPPING) {
    const rootItem = category.items?.find((i) => i.code === code);
    if (rootItem) return rootItem;
    for (const group of category.groups) {
      const item = group.items.find((i) => i.code === code);
      if (item) return item;
    }
  }
  return undefined;
}

export function getAllPrelimsItems(): PrelimsItem[] {
  return PRELIMS_MAPPING.flatMap((cat) => [
    ...(cat.items ?? []),
    ...cat.groups.flatMap((grp) => grp.items),
  ]);
}

export function findPrelimsItemByDescription(description: string): {
  category: string;
  group: string;
  item: PrelimsItem;
} | undefined {
  for (const cat of PRELIMS_MAPPING) {
    const rootItem = cat.items?.find((i) => i.description === description);
    if (rootItem)
      return { category: cat.label, group: cat.description ?? cat.label, item: rootItem };
    for (const grp of cat.groups) {
      const item = grp.items.find((i) => i.description === description);
      if (item) return { category: cat.label, group: grp.label, item };
    }
  }
  return undefined;
}

/**
 * Sums the Preliminary Items Bill Summary's Amount — Σ quantity × rate × markup,
 * where rate is the estimator's own saved rate when they entered one, otherwise
 * the item-library rate for the row's code (variant-aware, same as
 * `PrelimsBillSummary.tsx`). Mirrors `useRoadsAndSewersTotalSync.ts`'s
 * `prelimsTotal`, and is what the Run Matrix's Preliminaries section reconciles
 * against.
 *
 * The rate is rounded to 2dp — `qty × round(baseRate × factor, 2)` — before
 * being summed, same as `PrelimsBillSummary.tsx`'s own per-row Rate/Amount
 * columns and `useRoadsAndSewersTotalSync.ts`'s `prelimsTotal`. Summing the
 * unrounded `baseRate × factor` here (as this used to) drifts this total away
 * from the bill by the accumulated per-row rounding difference.
 */
export const sumPrelimsAmount = (
  savedItems: PrelimsItemRecord[] | undefined,
  getLibRate: (code: string) => number | undefined,
  markupFactor: number,
): number => {
  const savedById = new Map<string, PrelimsItemRecord>();
  (savedItems ?? []).forEach((item) => savedById.set(item.id, item));

  let total = 0;
  forEachPrelimsItem(savedById, (_ref, saved, item) => {
    const selectedVariant = item.variants?.find((v) => v.code === saved.code);
    const code = item.variants ? selectedVariant?.code ?? item.code : item.code;
    const libRate = getLibRate((code || '').trim().toUpperCase()) ?? 0;
    const baseRate = saved.rate > 0 ? saved.rate : libRate;
    const markedUpRate = parseFloat((baseRate * markupFactor).toFixed(2));
    total += saved.quantity * markedUpRate;
  });
  return total;
};

/** Where a prelims item sits in the bill: its category, and its group when it has one. */
export interface PrelimsItemRef {
  categoryId: string;
  categoryLabel: string;
  /** Undefined for items that hang directly off the category. */
  groupId?: string;
  groupLabel?: string;
}

/**
 * Walks every *measured* prelims item (saved quantity > 0) in bill order,
 * resolving the same ids — including the legacy `METHOD RELATED ITEMS` group ids
 * kept for tenders saved before those group headers were removed — that
 * `PrelimsBillSummary.tsx` and `PreliminaryItems.tsx` use. Single traversal
 * shared by `sumPrelimsAmount` and the Run Matrix's Preliminaries builder so
 * neither can miss a row the other finds.
 */
export const forEachPrelimsItem = (
  savedById: Map<string, PrelimsItemRecord>,
  visit: (ref: PrelimsItemRef, saved: PrelimsItemRecord, item: PrelimsItem) => void,
): void => {
  PRELIMS_MAPPING.forEach((cat) => {
    (cat.items ?? []).forEach((item) => {
      const idSuffix = item.description.replace(/[^a-zA-Z0-9]/g, '-');
      const legacyGroupId = LEGACY_METHOD_RELATED_GROUP_IDS[item.description];
      const saved =
        savedById.get(`prelim-${cat.id}-${idSuffix}`) ??
        (legacyGroupId ? savedById.get(`prelim-${cat.id}-${legacyGroupId}-${idSuffix}`) : undefined);
      if (!saved || saved.quantity <= 0) return;
      visit({ categoryId: cat.id, categoryLabel: cat.label }, saved, item);
    });

    cat.groups.forEach((grp) => {
      grp.items.forEach((item) => {
        const id = `prelim-${cat.id}-${grp.id}-${item.description.replace(/[^a-zA-Z0-9]/g, '-')}`;
        const saved = savedById.get(id);
        if (!saved || saved.quantity <= 0) return;
        visit(
          { categoryId: cat.id, categoryLabel: cat.label, groupId: grp.id, groupLabel: grp.label },
          saved,
          item,
        );
      });
    });
  });
};
